/**
 * AASB S2 Compliance Threshold Calculator
 * Client-side logic for checking mandatory climate reporting obligations
 * 
 * Based on Australian Accounting Standards Board S2 Climate-related Disclosures
 * Effective from 1 January 2025 for Group 1 entities
 */

/**
 * Calculate compliance thresholds and determine reporting group
 * @param {Object} params - The compliance check parameters
 * @param {number|null} params.consolidated_revenue - Annual consolidated revenue in AUD
 * @param {number|null} params.consolidated_assets - Total consolidated assets in AUD
 * @param {number|null} params.employee_count - Number of employees
 * @param {boolean} params.has_nger_obligations - Has NGER Act 2007 obligations
 * @param {string|null} params.nger_registration_number - NGER registration number (optional)
 * @param {boolean} params.is_scheme_or_super - Is a registered scheme or superannuation entity
 * @param {number|null} params.scheme_asset_value - Scheme/super asset value in AUD
 * @returns {Object} Compliance assessment result
 */
export const calculateComplianceThresholds = ({
  consolidated_revenue,
  consolidated_assets,
  employee_count,
  has_nger_obligations,
  nger_registration_number,
  is_scheme_or_super,
  scheme_asset_value
}) => {
  const thresholds_met = [];
  let compliance_group = null;
  let compliance_start_date = null;

  // Threshold 1: Size criteria (need 2 of 3)
  let size_criteria_met = 0;
  
  if (consolidated_revenue && consolidated_revenue >= 50_000_000) {
    size_criteria_met += 1;
  }
  if (consolidated_assets && consolidated_assets >= 25_000_000) {
    size_criteria_met += 1;
  }
  if (employee_count && employee_count >= 100) {
    size_criteria_met += 1;
  }

  const meets_size_threshold = size_criteria_met >= 2;
  
  if (meets_size_threshold) {
    thresholds_met.push("Size threshold (2+ of: Revenue ≥$50M, Assets ≥$25M, Employees ≥100)");
    
    // Determine group based on size
    if ((consolidated_revenue && consolidated_revenue >= 500_000_000) ||
        (consolidated_assets && consolidated_assets >= 1_000_000_000)) {
      compliance_group = "group_1";
      compliance_start_date = "2025-01-01";
    } else if ((consolidated_revenue && consolidated_revenue >= 200_000_000) ||
               (consolidated_assets && consolidated_assets >= 500_000_000)) {
      compliance_group = "group_2";
      compliance_start_date = "2026-07-01";
    } else {
      compliance_group = "group_3";
      compliance_start_date = "2027-07-01";
    }
  }

  // Threshold 2: NGER obligations
  if (has_nger_obligations) {
    thresholds_met.push("NGER reporting obligations");
    if (!compliance_group) {
      compliance_group = "group_1";
      compliance_start_date = "2025-01-01";
    }
  }

  // Threshold 3: Scheme/super asset value
  if (is_scheme_or_super && scheme_asset_value && scheme_asset_value >= 5_000_000_000) {
    thresholds_met.push("Scheme/Super assets ≥$5B");
    if (!compliance_group) {
      compliance_group = "group_1";
      compliance_start_date = "2025-01-01";
    }
  }

  const is_reporting_entity = thresholds_met.length > 0;
  const meets_scheme_threshold = is_scheme_or_super && scheme_asset_value && scheme_asset_value >= 5_000_000_000;

  // Generate recommendation
  let recommendation;
  if (is_reporting_entity) {
    const group_names = {
      "group_1": "Group 1 (largest entities)",
      "group_2": "Group 2 (large entities)",
      "group_3": "Group 3 (medium entities)"
    };
    recommendation = `Your organisation is classified as a ${group_names[compliance_group] || 'reporting entity'}. ` +
      `Mandatory AASB S2 climate reporting begins for financial years starting on or after ${compliance_start_date}. ` +
      `We recommend starting preparation now to ensure compliance.`;
  } else {
    recommendation = "Based on the information provided, your organisation does not currently meet the mandatory " +
      "reporting thresholds. However, voluntary reporting under AASB S1/S2 can enhance your credibility " +
      "with investors and customers. Consider adopting the framework proactively.";
  }

  return {
    is_reporting_entity,
    compliance_group,
    compliance_start_date,
    thresholds_met,
    recommendation,
    // Additional fields for database storage
    meets_size_threshold,
    meets_scheme_threshold
  };
};

/**
 * Get compliance group display information
 * @param {string} group - The compliance group (group_1, group_2, group_3)
 * @returns {Object} Display information for the group
 */
export const getComplianceGroupInfo = (group) => {
  const groups = {
    'group_1': { 
      label: 'Group 1', 
      color: 'bg-red-100 text-red-800', 
      desc: 'Largest entities',
      startDate: '1 January 2025'
    },
    'group_2': { 
      label: 'Group 2', 
      color: 'bg-amber-100 text-amber-800', 
      desc: 'Large entities',
      startDate: '1 July 2026'
    },
    'group_3': { 
      label: 'Group 3', 
      color: 'bg-blue-100 text-blue-800', 
      desc: 'Medium entities',
      startDate: '1 July 2027'
    }
  };
  return groups[group] || { label: 'Exempt', color: 'bg-green-100 text-green-800', desc: 'Not required', startDate: null };
};
